/**
 * Where the app is, as a URL, and back.
 *
 *   /org-aboveboard/venue-collingwood/recipes/negroni
 *    └── org ─────┘ └── venue ──────┘ └ screen ┘└ its subject
 *
 *   { kind: "recipes", params: { specId: "negroni" } }   ⇄   …/recipes/negroni
 *   { kind: "org", params: { tab: "integrations" } }      ⇄   …/organisation/integrations
 *   { kind: "prep", params: { tab: "Closing" } }          ⇄   …/prep?tab=Closing
 *
 * Pure and free of React so it can be tested on its own; App.tsx owns the
 * browser history and decides what an org or venue the person cannot reach
 * means. CloudFront already answers every path with index.html, so a deep
 * link or a reload needs nothing from the hosting.
 *
 * **Org and venue lead the path** (#124) so a link opens what it was copied
 * from: the same organisation for somebody in several, the same venue once
 * an organisation has two (#92). Both are optional when reading — a link from
 * before they were added, `/recipes/negroni`, still opens, and App.tsx
 * rewrites it in full. The venue is omitted when the person has none.
 *
 * Telling them apart: the first segment is an org unless it is a screen's
 * path, and the second a venue unless it is a screen's path. Ids are slugs
 * with a random suffix (`newVenueId`), so an org or venue whose id is
 * exactly `recipes` is not a case worth a more awkward URL.
 *
 * The screen's path names it in words a person would type or read in a
 * shared link, not the internal `kind`. One param per screen may sit in the
 * path — the thing the screen is about — and the rest go in the query.
 * `transient` params are never written to the address bar: they are
 * hand-offs between screens (the flavour wheel's drafted question), and a
 * reload repeating one would be a surprise.
 */

import type { ScreenState } from "./context.js";

export interface Route {
  /** The screen's `kind`, as `go()` and the registry know it. */
  readonly kind: string;
  /** The screen's path segment; empty for the dashboard. */
  readonly path: string;
  /** The param carried as the next path segment, if any. */
  readonly pathParam?: string;
  /** Params kept in memory but left out of the URL. */
  readonly transient?: readonly string[];
}

export const ROUTES: readonly Route[] = [
  { kind: "dash", path: "" },
  { kind: "platform", path: "platform" },
  { kind: "recipes", path: "recipes", pathParam: "specId" },
  { kind: "drinks", path: "drinks" },
  { kind: "wheel", path: "wheel" },
  { kind: "chat", path: "development", pathParam: "chatId", transient: ["prompt", "fresh"] },
  { kind: "count", path: "stocktake" },
  { kind: "prep", path: "prep" },
  { kind: "menu", path: "menu" },
  { kind: "waste", path: "wastage" },
  { kind: "queue", path: "queue" },
  { kind: "whattail", path: "whattail" },
  { kind: "guests", path: "guests", pathParam: "guestId" },
  { kind: "sup", path: "suppliers" },
  { kind: "order", path: "orders" },
  { kind: "exp", path: "expenses" },
  { kind: "cashup", path: "cash-up" },
  { kind: "sales", path: "sales" },
  { kind: "org", path: "organisation", pathParam: "tab" },
  { kind: "books", path: "books" },
  { kind: "training", path: "training", pathParam: "moduleId" },
];

/** The screen with no path of its own. */
export const HOME: ScreenState = { kind: "dash" };

/** Everything the address bar says. */
export interface AppLocation {
  readonly orgId?: string | undefined;
  readonly venueId?: string | undefined;
  readonly screen: ScreenState;
}

const SCREEN_PATHS: ReadonlySet<string> = new Set(ROUTES.map((r) => r.path).filter(Boolean));

const enc = encodeURIComponent;

/** The screen's part of the path and its query, with no leading org or venue. */
function screenPart(screen: ScreenState): { readonly segments: string[]; readonly search: string } {
  const route = ROUTES.find((r) => r.kind === screen.kind) ?? ROUTES[0]!;
  const segments = route.path ? [route.path] : [];
  const lead = route.pathParam ? screen.params?.[route.pathParam] : undefined;
  // The dashboard takes no path param; one on it would read as a screen.
  if (lead && route.path) segments.push(lead);

  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(screen.params ?? {})) {
    if (value === undefined || value === "") continue;
    if (route.transient?.includes(name)) continue;
    if (name === route.pathParam) continue;
    query.set(name, value);
  }
  return { segments, search: query.toString() };
}

/** The address for a location. An unknown screen is the dashboard. */
export function locationToUrl(location: AppLocation): string {
  const { segments, search } = screenPart(location.screen);
  const lead = location.orgId ? [location.orgId, ...(location.venueId ? [location.venueId] : [])] : [];
  const path = `/${[...lead, ...segments].map(enc).join("/")}`;
  return search ? `${path}?${search}` : path;
}

/** The address for a screen alone, as a link from before org and venue led the path. */
export function screenToUrl(screen: ScreenState): string {
  return locationToUrl({ screen });
}

function decode(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/** The screen a path's remaining segments and its query name, or null. */
function readScreen(segments: readonly string[], search: string): ScreenState | null {
  const route = ROUTES.find((r) => r.path === (segments[0] ?? ""));
  if (!route) return null;
  if (segments.length > (route.pathParam && route.path ? 2 : route.path ? 1 : 0)) return null;

  const params: Record<string, string> = {};
  for (const [name, value] of new URLSearchParams(search)) {
    if (value !== "") params[name] = value;
  }
  if (route.pathParam && segments[1]) params[route.pathParam] = segments[1];

  return Object.keys(params).length > 0 ? { kind: route.kind, params } : { kind: route.kind };
}

/**
 * The location an address names, or null when it names none — an unknown
 * screen, a segment too many, a malformed escape. The caller decides what
 * null means (App.tsx goes home and rewrites the address, so a stale
 * bookmark does not leave nonsense in the bar).
 */
export function urlToLocation(pathname: string, search: string): AppLocation | null {
  const segments: string[] = [];
  for (const raw of pathname.split("/")) {
    if (!raw) continue;
    const segment = decode(raw);
    if (segment === null) return null;
    segments.push(segment);
  }

  let rest = segments;
  let orgId: string | undefined;
  let venueId: string | undefined;
  if (rest[0] !== undefined && !SCREEN_PATHS.has(rest[0])) {
    orgId = rest[0];
    rest = rest.slice(1);
    if (rest[0] !== undefined && !SCREEN_PATHS.has(rest[0])) {
      venueId = rest[0];
      rest = rest.slice(1);
    }
  }

  const screen = readScreen(rest, search);
  if (!screen) return null;
  return { ...(orgId ? { orgId } : {}), ...(venueId ? { venueId } : {}), screen };
}

/** The screen an address names, ignoring any org and venue, or null. */
export function urlToScreen(pathname: string, search: string): ScreenState | null {
  return urlToLocation(pathname, search)?.screen ?? null;
}
